/**
 * Type Transformers - Pure Functions
 *
 * Pure transformation functions for converting API types to frontend domain types.
 * All functions are side-effect free and testable in isolation.
 */

import type { Pickem, PickemQuestion } from '@bet99-pickem/api-types';
import type { Competition, Question, GameState } from '@/types/domain';
import { getQuestionId, getOptionId } from './id-helpers';
import { getLocalizedField } from './localized-field';
import i18n from '@/i18n';

// =============================================================================
// Main Transformation Function
// =============================================================================

/**
 * Transforms a Pickem API response to a Competition domain object
 *
 * @param pickem - The pickem object from the API
 * @returns Transformed Competition object
 */
export function transformPickemToCompetition(pickem: Pickem): Competition {
  // Create a valid date from start field or start_utc_timestamp
  const dateString = pickem.start;
  const gameDate = dateString
    ? new Date(dateString)
    : pickem.start_utc_timestamp
      ? new Date(pickem.start_utc_timestamp * 1000)
      : new Date();

  const gameState = mapPickemStatusToGameState(pickem.status, gameDate);

  // Get current locale
  const locale = i18n.language;

  return {
    id: pickem.pickem_id,
    title: getLocalizedField(pickem, 'title', locale),
    prize: getLocalizedField(pickem, 'prize_text', locale) || 'Prize Pool',
    state: gameState,
    gameDate,
    finalScore:
      typeof pickem.user_ranking === 'number' && typeof pickem.total_points === 'number'
        ? {
            rank: pickem.user_ranking,
            points: pickem.total_points,
          }
        : undefined,
    currentPoints:
      typeof pickem.total_points === 'number' && gameState === 'live'
        ? pickem.total_points
        : undefined,
    correctPicks: pickem.correct_picks,
    questions: transformQuestions(pickem.questions),
    tieBreaker: extractTieBreaker(pickem.questions),
    hasSelections: pickem.has_entered,
    isOpen: pickem.open ?? true,
  };
}

// =============================================================================
// Game State Mapping
// =============================================================================

/**
 * Maps Pickem API status to frontend GameState
 *
 * @param status - Status from API (can be string or number)
 * @param startDate - The start date of the game
 * @returns The appropriate GameState
 */
export function mapPickemStatusToGameState(status: unknown, startDate: Date): GameState {
  // Normalize status to uppercase string
  const statusStr = String(status).toUpperCase();

  // If completed, always return final
  if (
    statusStr === 'COMPLETE' ||
    statusStr === 'COMPLETED' ||
    statusStr === 'FINAL' ||
    status === 4
  ) {
    return 'final';
  }

  // Check if start date is in the future
  const now = new Date();
  const isInFuture = startDate > now;

  // If in future and status is ACTIVE, show countdown
  if (isInFuture && (statusStr === 'ACTIVE' || status === 1 || status === 2)) {
    return 'countdown';
  }

  // If in past and status is ACTIVE, show live
  if (!isInFuture && (statusStr === 'ACTIVE' || statusStr === 'LIVE' || status === 3)) {
    return 'live';
  }

  // Default to countdown
  return 'countdown';
}

// =============================================================================
// Question Transformations
// =============================================================================

/**
 * Transforms array of PickemQuestions to frontend Question objects
 * Filters out tiebreaker questions
 *
 * @param questions - Array of questions from API
 * @returns Array of transformed Question objects
 */
export function transformQuestions(questions: PickemQuestion[]): Question[] {
  if (!questions || !Array.isArray(questions)) return [];

  // Get current locale
  const locale = i18n.language;

  return questions
    .filter(q => {
      // Filter out tiebreaker questions using multiple detection methods
      // Primary: question_type === 'tiebreaker'
      // Fallback: is_tiebreak flag
      return q.question_type !== 'tiebreaker' && !q.is_tiebreak;
    })
    .map(q => ({
      id: getQuestionId(q),
      type: q.category === 'prop' ? 'prop' : 'team',
      teams: extractTeamIds(q),
      question: q.category === 'prop' ? getLocalizedField(q, 'title', locale) : undefined,
      points: `${q.points}pts`,
      percentages: calculatePercentages(q),
    }));
}

/**
 * Extracts team IDs from question options
 * Returns first two option IDs as a tuple
 *
 * @param question - The question object
 * @returns Tuple of two team IDs
 */
export function extractTeamIds(question: PickemQuestion): [string, string] {
  const teamIds = question.options.slice(0, 2).map(opt => getOptionId(opt));

  return [teamIds[0] || 'team1', teamIds[1] || 'team2'];
}

/**
 * Calculates yes/no percentages for prop questions
 * Returns undefined for non-prop questions
 *
 * @param question - The question object
 * @returns Percentages object or undefined
 */
export function calculatePercentages(
  question: PickemQuestion
): { yes: number; no: number } | undefined {
  if (question.category !== 'prop') return undefined;

  const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
  const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));

  return {
    yes: yesOption?.popularity_pct || 50,
    no: noOption?.popularity_pct || 50,
  };
}

// =============================================================================
// Tiebreaker Extraction
// =============================================================================

/**
 * Extracts tiebreaker information from questions array
 * Returns default tiebreaker if none found
 *
 * @param questions - Array of questions from API
 * @returns Tiebreaker configuration object
 */
export function extractTieBreaker(questions: PickemQuestion[]): Competition['tieBreaker'] {
  if (!questions || !Array.isArray(questions)) {
    return {
      description: 'Predict the total score',
      teams: ['team1', 'team2'],
    };
  }

  const tieBreaker = questions.find(q => q.question_type === 'tiebreaker' || q.is_tiebreak);

  if (tieBreaker) {
    // Get current locale
    const locale = i18n.language;

    return {
      description:
        getLocalizedField(tieBreaker, 'description', locale) ||
        getLocalizedField(tieBreaker, 'title', locale),
      teams: extractTeamIds(tieBreaker),
    };
  }

  // Default tiebreaker if none found
  return {
    description: 'Predict the total score',
    teams: ['team1', 'team2'],
  };
}
